import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@isophi/core-legacy';
import { map, Observable } from 'rxjs';

import { UserService } from '../core/services/user.service';
import { toCamelcaseFun, toSnakecaseFun } from '../core/utils/object.utils';

const EXCLUDED_URLS = [
  '/token/',
  '/logged-user/',
  // snake_case consumed by User/Teacher.deserialize; `?` keeps /logged-teacher/mark-management-app-visited/ camelized
  '/logged-teacher/?',
  '/licenses/',
  '/portfolio/activities/print/',
  '/portfolio/targets/print/',
  '/portfolio-manager-reports/activity/',
  '/portfolio-manager-reports/target/',
];

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  private readonly defaultLang = 'cs';

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let headers = req.headers;

    headers = headers.set('Accept-Language', this.resolveLanguageHeader());
    headers = headers.set('X-Timezone', this.timezone);

    if (!headers.get('Authorization') && this.authService.accessToken) {
      headers = headers.set('Authorization', 'Bearer ' + this.authService.accessToken);
    }

    // Add current kindergarten header unless explicitly provided
    if (!headers.has('X-Current-Kindergarten-UUID')) {
      const currentKindergartenUUID = this.userService.getCurrentKindergartenUUID();
      if (currentKindergartenUUID) {
        headers = headers.set('X-Current-Kindergarten-UUID', currentKindergartenUUID);
      }
    }

    const body = req.body !== null && typeof req.body === 'object' && !(req.body instanceof FormData) ? toSnakecaseFun(req.body) : req.body;

    return next.handle(req.clone({ headers, body })).pipe(map(this.convertResponse));
  }

  convertResponse = (event: HttpEvent<unknown>) => {
    if (event instanceof HttpResponse && event.body && !this.hasExcludedUrl(event.url)) {
      const contentType = event.headers.get('content-type');
      if (
        contentType &&
        (contentType.startsWith('application/zip') ||
          contentType.startsWith('application/pdf') ||
          contentType.startsWith('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'))
      ) {
        return event;
      }

      return event.clone({ body: toCamelcaseFun(event.body) });
    }
    return event;
  };

  hasExcludedUrl(url: string) {
    return EXCLUDED_URLS.some((exclUrl) => url.includes(exclUrl));
  }

  private resolveLanguageHeader(): string {
    if (typeof localStorage !== 'undefined') {
      const persistedLang = localStorage.getItem('translocoLang');
      if (persistedLang) {
        return persistedLang;
      }
    }

    if (typeof document !== 'undefined' && document.documentElement?.lang) {
      return document.documentElement.lang;
    }

    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language.slice(0, 2);
    }

    return this.defaultLang;
  }
}
